import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Film, AlertCircle, ArrowUpRight, Layers, FileVideo, Clapperboard, Camera } from 'lucide-react';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState('individual');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, register, signInWithOAuth } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithOAuth('google');
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };
  // Clear error when switching between login/register or when user types
  const clearError = useCallback(() => {
    if (error) setError('');
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, accountType);
      }
      navigate('/');
    } catch (err) {
      // Display user-friendly error message from AuthContext
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side — Cinerian / FrameFlow brand panel.
          Mirrors the marketing site (cinerian.com) so the auth screen feels
          like part of the same product family rather than a generic photo. */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950 text-zinc-100"
        data-testid="auth-brand-panel"
      >
        {/* Subtle radial backdrop + grid lines for depth */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(234,88,12,0.18),transparent_55%)]" aria-hidden="true" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(234,88,12,0.10),transparent_60%)]" aria-hidden="true" />
        {/* Decorative film-reel rings — echo cinerian.com's motif */}
        <svg
          className="absolute -left-32 top-1/3 h-[480px] w-[480px] text-primary/15 pointer-events-none"
          viewBox="0 0 200 200"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="100" cy="100" r="92" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="70" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="48" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="26" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="38" r="6" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="162" r="6" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="38" cy="100" r="6" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="162" cy="100" r="6" stroke="currentColor" strokeWidth="0.6" />
        </svg>
        <svg
          className="absolute -right-40 -bottom-24 h-[520px] w-[520px] text-primary/10 pointer-events-none"
          viewBox="0 0 200 200"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="100" cy="100" r="92" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="70" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="48" stroke="currentColor" strokeWidth="0.6" />
          <circle cx="100" cy="100" r="26" stroke="currentColor" strokeWidth="0.6" />
        </svg>

        {/* Content column */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full max-w-[560px] mx-auto">
          {/* Top — Cinerian wordmark + product pill */}
          <div>
            <a
              href="https://cinerian.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-zinc-100 hover:text-primary transition-colors group"
              data-testid="auth-cinerian-link"
            >
              <span className="font-display text-xl font-semibold tracking-tight">Cinerian</span>
              <ArrowUpRight className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

          {/* Middle — headline + subline + hierarchy chips */}
          <div className="my-10 xl:my-14">
            <h2 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
              Build your film —
              <br />
              <span className="text-primary italic">from script to shoot.</span>
            </h2>
            <p className="mt-6 text-sm xl:text-base text-zinc-400 leading-relaxed max-w-[460px]">
              FrameFlow turns your screenplay into a structured production workflow — built around how films are actually made.
            </p>

            {/* Production hierarchy — Workspace → Film → Scene → Shot */}
            <ul className="mt-8 flex flex-wrap items-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-500">
              {[
                { icon: Layers, label: 'Workspace' },
                { icon: FileVideo, label: 'Film' },
                { icon: Clapperboard, label: 'Scene' },
                { icon: Camera, label: 'Shot' },
              ].map((step, idx, arr) => (
                <React.Fragment key={step.label}>
                  <li className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5 text-zinc-300">
                    <step.icon className="h-3 w-3 text-primary/80" />
                    {step.label}
                  </li>
                  {idx < arr.length - 1 && (
                    <span className="text-zinc-700 text-xs" aria-hidden="true">→</span>
                  )}
                </React.Fragment>
              ))}
            </ul>

            {/* Mini "scene card" mockup — echoes the cinerian.com hero device */}
            <div className="mt-10 rounded-xl border border-zinc-800 bg-zinc-900/60 backdrop-blur-sm p-4 max-w-[420px] shadow-lg shadow-black/30">
              <div className="flex items-center gap-1.5 mb-3">
                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                <span className="ml-2 text-[10px] text-zinc-500 font-mono tracking-tight truncate">
                  frameflow.cinerian.com / The Coastline
                </span>
              </div>
              <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500 mb-1">
                Scene 12
              </p>
              <p className="text-sm font-medium text-zinc-100 mb-3">
                INT. Lighthouse — Night
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: '12A', state: 'done' },
                  { id: '12B', state: 'done' },
                  { id: '12C', state: 'prod' },
                  { id: '12D', state: 'todo' },
                  { id: '12E', state: 'todo' },
                ].map((shot) => {
                  const cls =
                    shot.state === 'done'
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                      : shot.state === 'prod'
                      ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                      : 'border-zinc-700 bg-zinc-800/40 text-zinc-400';
                  return (
                    <span
                      key={shot.id}
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${cls}`}
                    >
                      {shot.id}
                    </span>
                  );
                })}
              </div>
              <p className="mt-3 text-[10px] text-zinc-500">
                2 / 5 complete · Day 4 of 18
              </p>
            </div>
          </div>

          {/* Bottom — footnote */}
          <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-zinc-500">
            Free to start · No credit card required
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-sm bg-primary flex items-center justify-center">
                <Film className="h-10 w-10 text-primary-foreground" />
              </div>
            </div>
            <h1 className="text-4xl font-bold font-[Manrope] tracking-tight">
              FrameFlow
            </h1>
            <p className="text-muted-foreground">Professional film production workflow</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="auth-name-input"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); clearError(); }}
                    required={!isLogin}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      type="button"
                      onClick={() => setAccountType('individual')}
                      className={`p-4 border rounded-sm text-left transition-colors ${
                        accountType === 'individual'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="account-type-individual"
                    >
                      <div className="font-semibold">Individual / Team</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Personal projects &amp; workspace collaboration
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError(); }}
                required
                className={error && (error.toLowerCase().includes('email')) ? 'border-destructive' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError(); }}
                required
                minLength={isLogin ? undefined : 6}
                className={error && (error.toLowerCase().includes('password')) ? 'border-destructive' : ''}
              />
              {!isLogin && (
                <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20" data-testid="auth-error">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              data-testid="google-signin-button"
              className="w-full"
              disabled={googleLoading}
              onClick={handleGoogleSignIn}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {googleLoading ? 'Redirecting...' : 'Google'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                data-testid="auth-toggle-button"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleToggleMode}
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>

            {/* Signup-only consent footnote. We surface it ONLY in signup
                mode because that's where account creation actually happens;
                showing it on the login form would imply re-consent on every
                login, which is misleading. */}
            {!isLogin && (
              <p
                className="text-center text-xs text-muted-foreground leading-relaxed px-2"
                data-testid="auth-consent-footnote"
              >
                By signing up, you agree to our{' '}
                <Link
                  to="/terms"
                  className="text-primary hover:text-primary/80 underline underline-offset-2"
                  data-testid="auth-terms-link"
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link
                  to="/privacy"
                  className="text-primary hover:text-primary/80 underline underline-offset-2"
                  data-testid="auth-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Auth;
